import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef, HostBinding, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { startWith, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export class SeAutocompleteStateMatcher implements ErrorStateMatcher {
    isOnError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    required: boolean = false;

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        let _isOnError = !!(control && control.invalid && this.required);
        if (_isOnError != this.isOnError.value) {
            this.isOnError.next(_isOnError);
        }

        return _isOnError;
    }
}

export enum dynamicReasearchType {
    none = 0,
    elastic = 1,
}

@Component({
    selector: 'se-autocomplete',
    templateUrl: './se-autocomplete.component.html',
    styleUrls: ['./se-autocomplete.component.scss'],
})
export class SeAutocompleteComponent implements OnDestroy, OnInit {
    @HostBinding('style')
    display = 'flex';

    @ViewChild('textInput', { static: true }) textInput: ElementRef<HTMLInputElement>;

    formControl = new FormControl();
    filteredOptions: Observable<string[]>;
    showAddButton: boolean = false;

    seAutocompleteMatcher: SeAutocompleteStateMatcher = new SeAutocompleteStateMatcher();

    prompt = this.translateService.instant('PressEnterToAdd') + ' "';

    private _isOnError: boolean = false;
    private _isOnErrorSubscriber: Subscription;

    @Output() itemAddRequired = new EventEmitter();

    @Output() isOnErrorChange = new EventEmitter();

    @Input()
    get isOnError() {
        return this._isOnError;
    }
    set isOnError(value) {
        if (this._isOnError != value) {
            this._isOnError = value;

            setTimeout(() => {
                // Il timeout serve per la sincronizzazione altrimenti abbiamo l'errore i debug
                this.isOnErrorChange.emit(value);
            }, 0);
        }
    }

    @Input()
    dynamicAddEnabled: boolean = false; // Abilita\disabilita l'aggiunta dinamina all'elenco degli item

    @Input()
    isWithLabel: boolean = true;

    @Input()
    dynamicReasearchType: dynamicReasearchType = dynamicReasearchType.none;

    @Input()
    required: boolean = false;

    _disabled: boolean = false;
    @Input()
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        if (this._disabled != value) {
            this._disabled = value;

            this._disabled ? this.formControl.disable() : this.formControl.enable();
        }
    }

    @Input()
    placeholder;

    @Input()
    items: string[] = []; // Frasi predefinite

    @Output() valueChange = new EventEmitter();
    _value: any; // Valore selezionati o inserito
    @Input()
    get value() {
        return this._value;
    }
    set value(value) {
        if (this._value != value) {
            this._value = value;

            this.valueChange.emit(this._value);
        }
    }

    @Input()
    icon?: string | null;

    @Input()
    iconTooltip?: string | null;

    @Output() optionsClickedEvent = new EventEmitter<string>();

    constructor(private translateService: TranslateService) {}

    ngOnInit(): void {
        this.seAutocompleteMatcher.required = this.required;

        this.filteredOptions = this.formControl.valueChanges.pipe(
            startWith(!!this.value && this.dynamicAddEnabled ? this.value : ''),
            map((item) => (item ? this.filterItems(item) : this.items.slice()))
        );
    }

    ngOnDestroy() {
        if (this._isOnErrorSubscriber) {
            this._isOnErrorSubscriber.unsubscribe();
        }
    }

    filterItems(name: string) {
        let results = this.items.filter((item) => item.toLowerCase().indexOf(name.toLowerCase()) === 0);

        if (this.dynamicReasearchType == dynamicReasearchType.elastic) {
            /* Faccio la ricerca su elastic search -- aggiungo i risultati agli items e procedo*/
        }

        if (this.dynamicAddEnabled) {
            this.showAddButton = results.length === 0;
            if (this.showAddButton) {
                results = [this.prompt + name + '"'];
            }
        }

        return results;
    }

    optionSelected(option) {
        if (option.value.indexOf(this.prompt) === 0) {
            this.addOption();
        }
    }

    addOption() {
        if (this.dynamicAddEnabled) {
            let option = this.removePromptFromOption(this.formControl.value);
            if (!this.items.some((entry) => entry === option)) {
                const index = this.items.push(option) - 1;
                this.formControl.setValue(this.items[index]);

                this.itemAddRequired.emit(option);
            }
        }
    }

    removePromptFromOption(option) {
        if (option.startsWith(this.prompt)) {
            option = option.substring(this.prompt.length, option.length - 1);
        }
        return option;
    }

    getErrorMessage(): string {
        return this.translateService.instant('MandatoryData');
    }

    emitOptionValue(optionValue: string) {
        this.optionsClickedEvent.emit(optionValue);
    }

    // Funzioni di manipolazione dall'esterno

    focus() {
        this.textInput.nativeElement.focus();
    }

    select() {
        this.textInput.nativeElement.select();
    }
}
